/**
 * Error Message Component
 *
 * Displays error messages with optional retry action
 */

import clsx from 'clsx';
import { Button } from '@/catalyst-ui-kit/typescript/button';
import { getUserErrorMessage } from '@/lib/errorHandling';

export interface ErrorMessageProps {
  error: unknown;

  /** Optional title (default: "Error") */
  title?: string;

  /** Optional retry action */
  onRetry?: () => void;

  /** Retry button text (default: "Try Again") */
  retryText?: string;

  /** Center error message on page */
  centered?: boolean;

  /** Additional CSS classes */
  className?: string;
}

/**
 * ErrorMessage Component
 */
export function ErrorMessage({
  error,
  title = 'Error',
  onRetry,
  retryText = 'Try Again',
  centered = false,
  className,
}: ErrorMessageProps) {
  const message = getUserErrorMessage(error);

  // Ensure message is a string for rendering
  const displayMessage = typeof message === 'string' ? message : JSON.stringify(message);

  const content = (
    <div
      role="alert"
      className={clsx(
        'rounded-lg border border-red-200 bg-red-50 p-4',
        'dark:border-red-800 dark:bg-red-950',
        className
      )}
    >
      <h5 className="mb-1 font-medium text-red-900 dark:text-red-100">{title}</h5>
      <div className="text-sm text-red-800 dark:text-red-200 whitespace-pre-wrap">
        {displayMessage}
      </div>

      {onRetry && (
        <div className="mt-4">
          <Button onClick={onRetry}>{retryText}</Button>
        </div>
      )}
    </div>
  );

  if (centered) {
    return (
      <div className="flex min-h-100 items-center justify-center p-4">
        <div className="w-full max-w-none">{content}</div>
      </div>
    );
  }

  return content;
}
